import { Dispatch, SetStateAction, useState } from 'react';

type ManualEvent = {
  target: {
    name: string;
    value: string;
  };
};

export type FormChangeEvent =
  | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  | ManualEvent;
type ValidatorFn = (value: string) => string;
type ValidationRules = Record<string, ValidatorFn | ValidatorFn[]>;

export const useFormValidation = <T extends Record<string, string>>(
  initialFormValues: T,
  rules: ValidationRules,
): {
  formValues: T;
  errors: Record<string, string>;
  handleChange: (e: FormChangeEvent) => void;
  validateAllFields: (valuesToValidate: T) => boolean;
  validateAll: () => boolean;
  setErrorsState: (newErrors: Record<string, string>) => void;
  setFormValues: Dispatch<SetStateAction<T>>;
} => {
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setErrorsState = (newErrors: Record<string, string>) => {
    setErrors((prev) => ({ ...prev, ...newErrors }));
  };

  const validate = (fieldName: string, value: string) => {
    const fieldRules = rules[fieldName];
    if (!fieldRules) return '';

    const ruleList: ValidatorFn[] = Array.isArray(fieldRules) ? fieldRules : [fieldRules];

    for (const rule of ruleList) {
      const error = rule(value);
      if (error) return error;
    }
    return '';
  };

  const handleChange = (e: FormChangeEvent) => {
    let { value } = e.target;
    const { name } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    // Special handling for purchasePrice to limit to two decimal places
    if (value?.includes('.') && name === 'purchasePrice') {
      const [integerPart, decimalPart] = value.split('.');
      if (decimalPart && decimalPart.length > 2) {
        value = `${integerPart}.${decimalPart.slice(0, 2)}`;
      }
    }

    const error = validate(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateAll = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    for (const [field, value] of Object.entries(formValues)) {
      const error = validate(field, value);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const validateAllFields = (valuesToValidate: typeof formValues) => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    for (const [field, value] of Object.entries(valuesToValidate)) {
      const fieldRules = rules[field];
      if (!fieldRules) continue;

      const ruleList = Array.isArray(fieldRules) ? fieldRules : [fieldRules];
      for (const rule of ruleList) {
        const error = rule(value);
        if (error) {
          newErrors[field] = error;
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      setErrorsState(newErrors);
    }
    return isValid;
  };

  return {
    formValues,
    errors,
    handleChange,
    validateAllFields,
    validateAll,
    setErrorsState,
    setFormValues,
  };
};
